/**
 * Recorded talking-head/voiceover → chat hand-off (behind
 * Flags.canUseToolbox()).
 *
 * Instead of the legacy JAM recommendation pipeline (recommendOverlays: true →
 * /api/generate-video-recommendation/), a finished recording — or an uploaded
 * recording file — is staged as a chat message for the chat agent, which
 * applies the asset (add_recorded_voiceover_scene_group /
 * add_talking_head_scene_group) and builds the scenes itself.
 */
import { VideoData } from '@lumen5/luminary';

import { resolveUploadedAssetFromRegistries } from 'js/src/agent/registries/resolveUploadedAsset';
import type { AudioAssetModel, SlideBackgroundModel } from 'js/src/client';
import { SlideBackgroundModelTypeEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    clearDashboardSketchData,
    saveDashboardSketchData,
    type SerializedMedia,
    type SerializedVoiceover,
} from 'js/src/components/dashboard/utils/dashboardSketchStorage';
import { SupportedFileType } from 'js/src/components/sketch/FileUpload';
import {
    EDITOR_TAB_OPTIONS,
    getSelectedAspectRatio,
    getSelectedBlueprintId,
    getSelectedTemplateId,
    switchEditorTabAction,
} from 'js/src/data/app';
import { getBlueprint } from 'js/src/data/blueprints';
import { markScenesBusy } from 'js/src/data/agent-activity';
import { parkVideoAndEnterChatOperation } from 'js/src/data/script-composer/script-composer-chat-handoff';
import { getUploadedAssetByUuid } from 'js/src/data/uploads';
import { requestChatHandoffConsume } from 'js/src/libs/chat-handoff-event-emitter';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Flags from 'js/src/libs/flags';
import type { AppDispatch } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import { isSceneAnOverlay } from 'js/src/luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    applyTalkingHeadMediaOperation,
    createNewVoicedrivenSceneGroupOperation,
    deleteSceneGroupOperation,
    setAndApplyVoiceoverAssetForSceneGroupOperation,
} from 'js/src/luminary/luminary-operations';

export interface RecordedMediaHandoffMessageOptions {
    isTalkingHead: boolean;
    templateId?: string | null;
    aspectRatio?: string | null;
    blueprint?: { id: number; name: string } | null;
}

/**
 * The outcome of a hand-off attempt. Callers must distinguish the two failure
 * modes from the success: `notApplicable` means this flow never applied and the
 * caller should carry on with its normal path, while `failed` means the hand-off
 * was attempted and broke — falling through to the legacy path there would run
 * the very `/api/generate-video-recommendation/` call the hand-off exists to
 * avoid, on an asset the user already saw a failure for.
 */
export type NarrationHandoffResult = 'handled' | 'notApplicable' | 'failed';

/**
 * Builds the message handed to the chat agent for the "create a whole video
 * from my recording" flows. One user-facing sentence, then the settings the
 * user already picked as plain parameters — the agent decides which tools to
 * call. The <video-request> block is stripped from the user's bubble by the
 * chat renderer, so only the first line is visible.
 */
export const buildRecordedMediaHandoffMessage = ({
    isTalkingHead,
    templateId,
    aspectRatio,
    blueprint,
}: RecordedMediaHandoffMessageOptions): string => {
    const narrationNoun = isTalkingHead ? 'talking head recording' : 'recorded voiceover';
    const lines: string[] = [
        `Create a video from my ${narrationNoun}.`,
        '',
        '<video-request>',
        `narration: the attached ${isTalkingHead ? 'video' : 'audio file'}, used exactly as recorded`,
    ];

    if (templateId) {
        lines.push(`template: ${templateId}`);
    }
    if (aspectRatio) {
        lines.push(`aspect ratio: ${aspectRatio}`);
    }
    if (blueprint) {
        lines.push(`blueprint: ${blueprint.name} (id ${blueprint.id})`);
    }

    lines.push('</video-request>');

    return lines.join('\n').trim();
};

/**
 * The recording travels as an attachment: a talking-head video as a media
 * attachment (re-registered with the MediaRegistry by UUID, giving the agent
 * the media_id it needs), a voiceover as an audio attachment (re-registered
 * with the VoiceoverRegistry, carrying the audio_uuid in its AUDIO metadata
 * block).
 */
const buildRecordingAttachments = (
    assetUuid: string,
    isTalkingHead: boolean,
): { media: SerializedMedia[]; voiceovers: SerializedVoiceover[] } => {
    if (isTalkingHead) {
        return {
            media: [
                {
                    id: uuid(),
                    name: 'Talking head recording',
                    type: SupportedFileType.MP4,
                    uuid: assetUuid,
                },
            ],
            voiceovers: [],
        };
    }
    return {
        media: [],
        voiceovers: [
            {
                id: uuid(),
                name: 'Voiceover recording',
                type: SupportedFileType.MP3,
                uuid: assetUuid,
            },
        ],
    };
};

/** Stages the hand-off message for the chat to consume, recording attached. */
const stageRecordedMediaMessage = (
    assetUuid: string,
    messageOptions: RecordedMediaHandoffMessageOptions,
): boolean => {
    const { media, voiceovers } = buildRecordingAttachments(
        assetUuid,
        messageOptions.isTalkingHead,
    );
    const messageText = buildRecordedMediaHandoffMessage(messageOptions);
    return saveDashboardSketchData({ messageText, documents: [], media, voiceovers });
};

/**
 * Hands a transcript-language change on an existing video to the chat.
 * The caller re-transcribes first (with recommendOverlays off) and only calls
 * this once the new transcript is loaded, so the agent rebuilds the scenes from
 * the corrected words rather than the stale ones. There is no attachment — the
 * narration asset is already on the scene group. Returns whether staging
 * succeeded.
 */
export const handOffTranscriptLanguageChangeToChat = ({
    isTalkingHead,
    dispatch,
}: {
    isTalkingHead: boolean;
    dispatch: AppDispatch;
}): boolean => {
    const narrationNoun = isTalkingHead ? 'talking head recording' : 'voiceover';
    const messageText = [
        `I corrected the transcription language for my ${narrationNoun}.`,
        '',
        '<video-request>',
        `narration: my ${narrationNoun}, still in place with a re-generated transcript`,
        'scenes: the overlays built from the old transcript have been removed',
        'to do: rebuild the supporting scenes from the corrected transcript',
        '</video-request>',
    ]
        .join('\n')
        .trim();

    if (!saveDashboardSketchData({ messageText, documents: [], media: [], voiceovers: [] })) {
        return false;
    }
    analytics.track('Transcript language change handed off to chat', {
        narrationType: isTalkingHead ? 'talking_head' : 'voiceover',
    });

    dispatch(switchEditorTabAction({ nextTab: EDITOR_TAB_OPTIONS.CHAT }));
    requestChatHandoffConsume();
    return true;
};

/**
 * Stages a dashboard repurpose upload ("Voiceover to video" / "Talking head
 * to video") for the chat, skipping the template-selection and
 * doc-to-script wizard entirely. The message is just the recording as an
 * attachment — no text; the agent's standard build flow handles an attached
 * recording on its own. Returns whether staging succeeded.
 */
export const stageDashboardRecordedMediaForChat = ({
    assetUuid,
    isTalkingHead,
}: {
    assetUuid: string;
    isTalkingHead: boolean;
}): boolean => {
    const { media, voiceovers } = buildRecordingAttachments(assetUuid, isTalkingHead);
    if (!saveDashboardSketchData({ messageText: '', documents: [], media, voiceovers })) {
        return false;
    }
    return true;
};

/**
 * The message for a narration scene group this client already created and
 * populated with the recording. The agent's only job is to fill the supporting
 * scenes, so the message carries no tool-call instructions or insertion index —
 * unlike the create-a-whole-video hand-offs, which still have to describe the
 * asset to apply.
 */
const buildNarrationHandoffMessage = (
    isTalkingHead: boolean,
    targetSceneGroupIndex: number,
): string => {
    const narrationNoun = isTalkingHead ? 'talking head recording' : 'recorded voiceover';
    return [
        `Add my ${narrationNoun}`,
        '',
        '<video-request>',
        `narration: my ${narrationNoun}, already added as a scene group with its transcript loaded`,
        `scene group index: ${targetSceneGroupIndex}`,
        'to do: build the supporting scenes from the transcript',
        '</video-request>',
    ]
        .join('\n')
        .trim();
};

/**
 * Adds an already-uploaded narration asset (a voiceover audio file or a
 * talking-head video — a finished recording, an uploads-tab drag, or a Voice-tab
 * insert) to the video, then asks the chat to populate it.
 *
 * The scene group is created and the asset applied here rather than by the
 * agent, so the recording lands at the drop position immediately instead of one
 * LLM round-trip later, and it cannot land at the wrong index (or not at all)
 * if the agent misreads the request. `recommendOverlays` is false throughout —
 * the supporting scenes are the agent's to build.
 *
 * Pass `replaceSceneGroupId` to swap the narration on a scene group that already
 * exists (dropping a recording onto an existing narration scene) instead of
 * inserting a new one — the drop target keeps its identity and position, and
 * only its narration asset changes.
 *
 * The message is staged *before* luminary is touched: staging is the one step
 * that can fail without leaving anything behind, and doing it first means a
 * quota-exceeded `sessionStorage` can't strand a half-built scene group with
 * base scenes blurred until a lint step that will never run.
 *
 * The base scenes are marked as building (blurred until the lint step) exactly
 * as the agent's own scene-group tools do, so the hand-off looks identical to an
 * agent-initiated one.
 */
export const addNarrationAndHandOffToChatOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            assetUuid,
            isTalkingHead,
            targetSceneGroupIndex,
            replaceSceneGroupId,
        }: {
            assetUuid: string;
            isTalkingHead: boolean;
            targetSceneGroupIndex: number;
            replaceSceneGroupId?: string;
        },
    ): Promise<NarrationHandoffResult> => {
        const parentActionGroup = uuid();
        const systemTriggered = false;

        // A freshly-finished recording is in the uploads slice; an uploads-tab drag or a
        // media-library pick lives only in the registries.
        const narrationAsset =
            getUploadedAssetByUuid(getState(), assetUuid) ??
            (await resolveUploadedAssetFromRegistries(assetUuid));
        if (!narrationAsset) {
            return 'failed';
        }

        // Staged first: nothing has been mutated yet, so a staging failure is a
        // clean no-op the caller can report without cleanup.
        if (
            !saveDashboardSketchData({
                messageText: buildNarrationHandoffMessage(isTalkingHead, targetSceneGroupIndex),
                documents: [],
                media: [],
                voiceovers: [],
            })
        ) {
            return 'failed';
        }

        let sceneGroupId: string;
        if (replaceSceneGroupId) {
            sceneGroupId = replaceSceneGroupId;
        } else {
            // Throws when the template has no default voicedriven scene design (after
            // its own Toast.error) — a failed hand-off, not a reason to fall through to
            // the legacy pipeline.
            try {
                ({ sceneGroupId } = call(
                    createNewVoicedrivenSceneGroupOperation,
                    isTalkingHead ? VideoData.CATEGORY_TALKING_HEAD : VideoData.CATEGORY_VOICEOVER,
                    targetSceneGroupIndex,
                    { deleteOtherScenes: false, setPlaceholderAsset: false, hasAvatar: false },
                    parentActionGroup,
                    systemTriggered,
                ));
            } catch (error) {
                ErrorReporter.capture(error);
                return 'failed';
            }
        }

        const applied = isTalkingHead
            ? await call(applyTalkingHeadMediaOperation, {
                  sceneGroupId,
                  media: narrationAsset as ImmutableMap<SlideBackgroundModel>,
                  language_code: undefined,
                  forceCaptionGeneration: false,
                  enhanceAudio: true,
                  recommendOverlays: false,
                  trimRequired: false,
                  systemTriggered,
                  parentActionGroup,
              })
            : await call(setAndApplyVoiceoverAssetForSceneGroupOperation, {
                  sceneGroupId,
                  audioAssetData: narrationAsset as ImmutableMap<AudioAssetModel>,
                  avatarAssetData: null,
                  captionsLanguage: null,
                  forceCaptionGeneration: false,
                  selectedBlueprintId: null,
                  generateRecommendedVideoResponse: null,
                  sceneRecommendations: null,
                  recommendOverlays: false,
                  isGlobalVideo: true,
                  systemTriggered,
                  parentActionGroup,
              });

        if (!applied) {
            // Only delete a group this operation created — a replace target was the
            // user's existing scene group and must survive a failed swap. Deleting the
            // only remaining scene group throws; in that case the empty group stays
            // rather than letting the cleanup error mask the real failure.
            if (!replaceSceneGroupId) {
                try {
                    call(
                        deleteSceneGroupOperation,
                        sceneGroupId,
                        systemTriggered,
                        parentActionGroup,
                    );
                } catch {
                    // The video is empty either way — report the original failure.
                }
            }
            clearDashboardSketchData();
            return 'failed';
        }

        // Blur the base scenes for the rest of the turn, matching the agent's own
        // scene-group tools: a voicedriven base scene carries the voice asset rather
        // than editable text/media, so no set_text/set_media fill ever clears it.
        const state = getState();
        const narrationSceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const baseSceneIds =
            narrationSceneGroup?.scenes
                .filter((scene) => !isSceneAnOverlay(state, scene))
                .map((scene) => scene.id) ?? [];
        markScenesBusy(dispatch, baseSceneIds);

        dispatch(switchEditorTabAction({ nextTab: EDITOR_TAB_OPTIONS.CHAT }));
        // The chat consumes staged messages on mount, but here it is already
        // mounted — signal it to consume this one; consuming is idempotent.
        requestChatHandoffConsume();
        return 'handled';
    },
);

/**
 * Applying a video with a voiceover to a talking-head scene (by clicking it in
 * the media tab, which targets the focused/selected scene rather than a drop
 * target) replaces that scene group's narration, which runs the legacy
 * recommendation pipeline
 * (setMediaAssetForSceneOperation → applyTalkingHeadMediaOperation with
 * recommendOverlays: true). For chat users the recording is handed to the chat
 * instead, which rebuilds the supporting scenes itself.
 *
 * Returns 'notApplicable' when this is not a chat user replacing narration on a
 * talking-head scene, so callers carry on with their normal apply path.
 */
export const handOffRecordingAppliedToTalkingHeadSceneOperation = createLuminaryOperation(
    async (
        { luminary, call },
        {
            sceneId,
            mediaSourceData,
            isSticker,
        }: {
            sceneId: string;
            mediaSourceData: ImmutableMap<SlideBackgroundModel> | undefined;
            isSticker: boolean;
        },
    ): Promise<NarrationHandoffResult> => {
        if (!Flags.canUseToolbox() || isSticker || !mediaSourceData) {
            return 'notApplicable';
        }
        const video = mediaSourceData.get(SlideBackgroundModelTypeEnum.VIDEO);
        const assetUuid = video?.get('uuid');
        if (!video?.get('has_voiceover') || !assetUuid) {
            return 'notApplicable';
        }
        const scene = luminary.getSceneById(sceneId);
        const sceneGroup = scene?.sceneGroup;
        if (scene?.sceneCategory !== VideoData.CATEGORY_TALKING_HEAD || !sceneGroup) {
            return 'notApplicable';
        }
        return await call(addNarrationAndHandOffToChatOperation, {
            assetUuid,
            isTalkingHead: true,
            targetSceneGroupIndex: sceneGroup.index,
            replaceSceneGroupId: sceneGroup.id,
        });
    },
);

/**
 * Hands a recording made (or uploaded) inside the creator to the chat:
 * waits for the upload, stages the hand-off message, switches to the chat tab,
 * and signals the chat to consume it. The agent inserts the scene group at
 * targetSceneGroupIndex — no placeholder scene group should be created.
 */
export const handOffRecordedMediaToChatOperation = createLuminaryOperation(
    async (
        { call },
        {
            uploadingPromise,
            isTalkingHead,
            targetSceneGroupIndex,
        }: {
            uploadingPromise: Promise<string[]>;
            isTalkingHead: boolean;
            targetSceneGroupIndex: number;
        },
    ): Promise<void> => {
        const loadingToastId = Toast.loading('Preparing your recording...');
        let assetUuid: string | undefined;
        try {
            [assetUuid] = await uploadingPromise;
        } catch (error) {
            ErrorReporter.capture(error);
        }
        if (!assetUuid) {
            Toast.error(
                'Sorry, something went wrong with your recording.',
                undefined,
                loadingToastId,
            );
            return;
        }

        const added = await call(addNarrationAndHandOffToChatOperation, {
            assetUuid,
            isTalkingHead,
            targetSceneGroupIndex,
        });
        if (added !== 'handled') {
            Toast.error(
                'Sorry, something went wrong with your recording.',
                undefined,
                loadingToastId,
            );
            return;
        }
        Toast.success('Your recording is ready', undefined, loadingToastId);
    },
);

/**
 * Hands a recording made (or uploaded) in the doc-to-script "create a video
 * from your recording" flow to the chat. Mirrors the script-composer
 * hand-off: the placeholder video is parked and the agent builds the whole
 * video from the recording. The upload must already be complete.
 */
export const handOffRecordedMediaNewVideoToChatOperation = createLuminaryOperation(
    async (
        { getState, call },
        { assetUuid, isTalkingHead }: { assetUuid: string; isTalkingHead: boolean },
    ) => {
        const state = getState();
        const selectedBlueprint = getBlueprint(state, getSelectedBlueprintId(state));
        const messageOptions: RecordedMediaHandoffMessageOptions = {
            isTalkingHead,
            templateId: getSelectedTemplateId(state),
            aspectRatio: getSelectedAspectRatio(state),
            blueprint: selectedBlueprint
                ? { id: selectedBlueprint.id, name: selectedBlueprint.name }
                : null,
        };
        if (!stageRecordedMediaMessage(assetUuid, messageOptions)) {
            Toast.error('Unable to start building your video. Please try again.');
            return false;
        }

        await call(parkVideoAndEnterChatOperation);
        return true;
    },
);
